/**
 * QualityApi — AI Quality dashboard (Observability Phase 2 PR-E):
 * `/admin/quality/steps|trend|recent`, joining ai_usage_log and
 * quality_scores.
 *
 * Extracted as its own mixin module (not folded into admin-platform-api.ts)
 * to stay under that file's file-size ratchet
 * (`scripts/ci/check_file_size_ratchet.py`). Methods-only by design:
 * prototype methods are merged onto AdminAPIClient via applyMixins in
 * client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';
import { transformKeysToCamelCase } from '@/utils/transform-keys';

import { BaseApiClient } from './base';

// ============================================================================
// AI Quality Dashboard Types
// ============================================================================

export interface QualityStep {
  pipeline: string;
  stage: string;
  canonicalId: string;
  agentName: string | null;
  calls: number;
  costUsd: number;
  p50Ms: number | null;
  p95Ms: number | null;
  scored: number;
  // Keys are raw snake_case score_name values (uniqueness, anomaly_count,
  // ci_groundedness, ci_specificity, ci_actionability) — never camelCased
  // (controller ruling R2). Restored post-transform in getQualitySteps.
  scores: Record<string, number>;
}

export interface QualityStepsResponse {
  steps: QualityStep[];
  periodDays: number;
  cached: boolean;
}

export interface QualityTrendPoint {
  date: string;
  pipeline: string;
  avg: number;
  p10: number;
  count: number;
}

export interface QualityTrendResponse {
  points: QualityTrendPoint[];
  periodDays: number;
  scoreName: string;
  cached: boolean;
}

export interface QualityRecentRow {
  createdAt: string;
  pipeline: string;
  stage: string;
  scoreName: string;
  value: number;
  rationale: string | null;
  contentId: string | null;
  scopeType: string | null;
  scopeId: string | null;
  traceId: string | null;
  langfuseExported: boolean;
}

export interface QualityRecentResponse {
  periodDays: number;
  rows: QualityRecentRow[];
}

export interface QualityRecentParams {
  days?: number;
  scoreName?: string;
  below?: number;
  limit?: number;
}

// ============================================================================
// AI Quality Dashboard Methods
// ============================================================================

export class QualityApi extends BaseApiClient {
  async getQualitySteps(days: number = 7): Promise<QualityStepsResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminQualitySteps}?days=${days}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<{ steps: Record<string, unknown>[] }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    const raw = result.data!;
    const transformed = transformKeysToCamelCase<QualityStepsResponse>(raw);
    // R2: scores map keys are score names (snake_case) and must not be
    // camelCased — restore them from the raw payload post-transform.
    transformed.steps = transformed.steps.map((step, i) => ({
      ...step,
      scores: (raw.steps[i]?.scores as Record<string, number>) ?? {},
    }));
    return transformed;
  }

  async getQualityTrend(days: number = 30, scoreName: string = 'uniqueness'): Promise<QualityTrendResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminQualityTrend}?days=${days}&score_name=${encodeURIComponent(scoreName)}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<QualityTrendResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<QualityTrendResponse>(result.data);
  }

  async getQualityRecent(params: QualityRecentParams = {}): Promise<QualityRecentResponse> {
    const { days = 7, scoreName, below = 0.3, limit = 50 } = params;
    const query = new URLSearchParams();
    query.set('days', String(days));
    if (scoreName) query.set('score_name', scoreName);
    query.set('below', String(below));
    query.set('limit', String(limit));

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminQualityRecent}?${query.toString()}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<QualityRecentResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return transformKeysToCamelCase<QualityRecentResponse>(result.data);
  }
}
